import isNil from 'lodash/isNil';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryParams, createEnumParam, NumberParam } from 'use-query-params';

import {
  type DashboardSortType,
  DASHBOARD_SORT_OPTIONS,
} from 'api/endpoints/rivals.types';
import { COMPANY_DEFAULT_ID } from 'pages/dashboard/partials/group-drop/GroupDropContent';
import store from 'store';

import type { Dispatch } from 'store/store.types';

export type DashboardFiltersType = {
  group?: number;
  sort?: DashboardSortType;
};

const DashboardSortParam = createEnumParam<DashboardSortType>([
  ...DASHBOARD_SORT_OPTIONS,
]);

const QueryParamConfig = {
  group: NumberParam,
  sort: DashboardSortParam,
};

const useDashboardFilter = () => {
  const {
    auth: { updateUserData },
  } = useDispatch<Dispatch>();
  const [query, setQuery] = useQueryParams(QueryParamConfig);

  const DASHBOARD_SORT_DEFAULT = 'cards';

  const userDataDefaults = useSelector(store.select.auth.userDataDefaults);
  const companyDataDefaults = useSelector(
    store.select.auth.companyDataDefaults,
  );

  const getInitialGroup = () => {
    if (query.group === 0 || userDataDefaults?.dashboard?.activeGroupId === 0)
      return 0;

    return (
      query.group ||
      userDataDefaults?.dashboard?.activeGroupId ||
      companyDataDefaults?.dashboard?.activeGroupId
    );
  };

  const getInitialSort = () => {
    const sort =
      query.sort ||
      userDataDefaults?.dashboard?.sortBy ||
      companyDataDefaults?.dashboard?.sortBy;

    return (isNil(sort) ? DASHBOARD_SORT_DEFAULT : sort) as DashboardSortType;
  };

  const [filter, setFilter] = useState<DashboardFiltersType>({
    group: getInitialGroup(),
    sort: getInitialSort(),
  });

  useEffect(() => {
    setQuery(filter, 'replaceIn');
  }, [filter, setQuery]);

  const rivalGroupId = query.group && query.group > 0 ? query.group : undefined;
  const rivalGroup = useSelector(store.select.rivalGroups.byId).get(
    rivalGroupId?.toString() || '',
  );

  return {
    ...useMemo(
      () => ({
        setGroup: (group: number | undefined) => {
          if (COMPANY_DEFAULT_ID === group) {
            group = companyDataDefaults?.dashboard?.activeGroupId;
          }

          if (isNil(group)) {
            group = -1;
          }

          setTimeout(() => {
            // Just to keep the UI responsive
            setFilter((filter) => ({ ...filter, group }));
            updateUserData({
              path: ['defaults', 'dashboard', 'activeGroupId'],
              value: group,
            });
          }, 100);
        },
        setSort: (sort: DashboardSortType) => {
          if ((sort as 'companyDefault') === 'companyDefault') {
            sort = companyDataDefaults?.dashboard?.sortBy as DashboardSortType;
          }

          if (isNil(sort)) {
            sort = 'cards';
          }

          setTimeout(() => {
            // Just to keep the UI responsive
            setFilter((filter) => ({ ...filter, sort }));
            updateUserData({
              path: ['defaults', 'dashboard', 'sortBy'],
              value: sort,
            });
          }, 100);
        },
      }),
      [
        companyDataDefaults?.dashboard?.activeGroupId,
        companyDataDefaults?.dashboard?.sortBy,
        updateUserData,
      ],
    ),
    ...filter,
    rivalGroup,
    sort: filter.sort || 'cards',
  };
};

export default useDashboardFilter;
